import React, { useState, useEffect } from 'react'
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Tabs,
  Tab,
  LinearProgress,
  Avatar,
  Snackbar,
  Alert as MuiAlert
} from '@mui/material'
import { API_BASE_URL } from '../config/api'
import {
  Visibility,
  Edit,
  Store,
  ShoppingCart,
  Warning,
  CheckCircle,
  Block,
  Close,
  Person,
  LocationOn,
  AttachMoney
} from '@mui/icons-material'
import ImageOverlay from '../components/ImageOverlay'

interface MarketplaceProduct {
  id: string
  product_name: string
  farmer_name: string
  farmer_id: string
  category: string
  variety: string
  quantity: number
  unit: string
  price_per_unit: number
  total_value: number
  status: 'active' | 'inactive' | 'draft' | 'sold' | 'expired' | 'suspended'
  listing_date: string
  expiry_date: string
  location: string
  organic_certified: boolean
  images_count: number
  harvest_images: any[]
  views: number
  inquiries: number
  orders: number
  issues_reported: number
}

interface MarketplaceOrder {
  id: string
  order_number: string
  buyer_name: string
  buyer_email?: string
  product_name: string
  farmer_name: string
  farmer_email?: string
  quantity: number
  unit: string
  unit_price?: number
  subtotal?: number
  delivery_fee?: number
  total_amount: number
  status: 'pending' | 'confirmed' | 'delivered' | 'cancelled' | 'disputed'
  order_date: string
  delivery_date?: string
  delivery_address?: string
  contact_phone?: string
  special_instructions?: string
  payment_status: 'pending' | 'paid' | 'failed' | 'refunded'
  payment_method?: string
}

interface FarmStoreProduct {
  id: string
  product_name: string
  farmer_name: string
  category_name: string
  unit_price: number
  unit_of_measure: string
  available_quantity: number
  minimum_order_quantity: number
  status: 'active' | 'inactive' | 'out_of_stock' | 'archived'
  is_featured: boolean
  views_count: number
  orders_count: number
  total_sold: number
  created_at: string
  published_at: string
  product_images?: any[]
}

const Marketplace: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0)
  const [products, setProducts] = useState<MarketplaceProduct[]>([])
  const [orders, setOrders] = useState<MarketplaceOrder[]>([])
  const [storeProducts, setStoreProducts] = useState<FarmStoreProduct[]>([])
  const [categories, setCategories] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedProduct, setSelectedProduct] = useState<MarketplaceProduct | null>(null)
  const [selectedStoreProduct, setSelectedStoreProduct] = useState<FarmStoreProduct | null>(null)
  const [viewProductDialog, setViewProductDialog] = useState(false)
  const [productDetails, setProductDetails] = useState<any>(null)
  const [actionDialog, setActionDialog] = useState(false)
  const [actionType, setActionType] = useState<'suspend' | 'activate' | 'delete'>('suspend')
  const [actionReason, setActionReason] = useState('')
  const [addingToStore, setAddingToStore] = useState(false)
  const [editStoreDialog, setEditStoreDialog] = useState(false)
  const [editPrice, setEditPrice] = useState('')
  const [editQuantity, setEditQuantity] = useState('')
  const [addToStoreDialog, setAddToStoreDialog] = useState(false)
  const [addToStoreProduct, setAddToStoreProduct] = useState<any>(null)
  const [imageOverlayOpen, setImageOverlayOpen] = useState(false)
  const [overlayImages, setOverlayImages] = useState<string[]>([])
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [storeFormData, setStoreFormData] = useState({
    product_name: '',
    category_id: '',
    unit_price: '',
    available_quantity: '',
    minimum_order_quantity: '1',
    status: 'active',
    is_featured: false
  })
  const [selectedOrder, setSelectedOrder] = useState<MarketplaceOrder | null>(null)
  const [viewOrderDialog, setViewOrderDialog] = useState(false)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }>({ open: false, message: '', severity: 'info' })

  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setSnackbar({ open: true, message, severity })
  }

  const resolveImageUrl = (img: any): string => {
    const url = typeof img === 'string' ? img : img?.url || ''
    if (!url) return ''
    return url.startsWith('http') ? url : `${API_BASE_URL.replace('/api/v1', '')}${url}`
  }

  const fetchMarketplaceData = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('token')
      
      // Fetch products from admin API instead of public search
      const productsResponse = await fetch(`${API_BASE_URL}/admin/products`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      // Fetch orders from admin API  
      const ordersResponse = await fetch(`${API_BASE_URL}/admin/orders`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      let productsData: any[] = []
      let ordersData: any[] = []

      if (productsResponse.ok) {
        const productsResult = await productsResponse.json()
        console.log('Products API response:', productsResult)
        
        // Check if response has success structure
        if (productsResult.success && productsResult.data) {
          // Extract products from the admin API response format
          productsData = productsResult.data.products || []
          console.log('Extracted products from success response:', productsData)
        } else {
          console.error('Unexpected API response structure:', productsResult)
          productsData = []
        }
        
        console.log('Is productsData an array?', Array.isArray(productsData))
        console.log('Type of productsData:', typeof productsData)
        console.log('Products data length:', productsData.length)
      } else {
        console.error('Failed to fetch products:', productsResponse.status, productsResponse.statusText)
        const errorText = await productsResponse.text()
        console.error('Error response body:', errorText)
        
        // Check if it's an authentication error
        if (productsResponse.status === 401) {
          console.error('Authentication failed - user may not be logged in as admin')
          showNotification('Authentication failed. Please login as an admin user.', 'error')
          // Could redirect to login here
        } else if (productsResponse.status === 403) {
          console.error('Access forbidden - user does not have admin role')
          showNotification('Access denied. Admin privileges required.', 'error')
        }
        
        productsData = []
      }

      if (ordersResponse.ok) {
        const ordersResult = await ordersResponse.json()
        console.log('Orders API response:', ordersResult)
        if (ordersResult.success && ordersResult.data) {
          ordersData = ordersResult.data.orders || []
        } else {
          ordersData = []
        }
      } else {
        console.error('Failed to fetch orders:', ordersResponse.status, ordersResponse.statusText)
        ordersData = []
      }

      // Ensure productsData is always an array
      if (!Array.isArray(productsData)) {
        console.error('productsData is not an array:', productsData)
        productsData = []
      }

      // Transform products data
      const transformedProducts: MarketplaceProduct[] = productsData.map((product: any) => {
        // Parse and merge images from harvest_images and product_images
        let harvestImages = product.harvest_images || [];
        let productImages = product.product_images || [];
        if (typeof harvestImages === 'string') {
          try { harvestImages = JSON.parse(harvestImages); } catch (e) { harvestImages = []; }
        }
        if (typeof productImages === 'string') {
          try { productImages = JSON.parse(productImages); } catch (e) { productImages = []; }
        }
        // Use product_images if harvest_images is empty, or vice versa
        const mergedImages = (Array.isArray(harvestImages) && harvestImages.length > 0) ? harvestImages
          : (Array.isArray(productImages) && productImages.length > 0) ? productImages : [];
        
        return {
          id: product.id || product.product_id,
          product_name: product.product_name || product.name,
          farmer_name: product.farmer?.name || product.farmer_name || 'Unknown Farmer',
          farmer_id: product.farmer_id,
          category: product.product_category || product.category || 'Uncategorized',
          variety: product.variety || product.variety_name || 'Unknown',
          quantity: product.available_quantity || product.quantity || 0,
          unit: product.unit || 'kg',
          price_per_unit: product.price_per_unit || product.price || 0,
          total_value: (product.price_per_unit || product.price || 0) * (product.available_quantity || product.quantity || 0),
          status: product.status || 'active',
          listing_date: product.listing_date || product.created_at || new Date().toISOString(),
          expiry_date: product.expiry_date || product.expires_at || new Date().toISOString(),
          location: product.location || product.farmer?.location || 'Unknown Location',
          organic_certified: product.organic_certified || false,
          harvest_images: mergedImages,
          images_count: mergedImages.length || 0,
          views: product.views || 0,
          inquiries: product.inquiries || 0,
          orders: parseInt(product.orders_count) || 0,
          issues_reported: product.issues_reported || 0
        };
      })

      // Transform orders data
      const transformedOrders: MarketplaceOrder[] = ordersData.map((order: any) => ({
        id: order.id || order.order_id,
        order_number: order.order_number || `ORD-${order.id}`,
        buyer_name: order.buyer?.name || order.buyer_name || 'Unknown Buyer',
        buyer_email: order.buyer_email,
        product_name: order.product?.name || order.product_name || 'Unknown Product',
        farmer_name: order.farmer?.name || order.farmer_name || 'Unknown Farmer',
        farmer_email: order.farmer_email,
        quantity: order.quantity || 0,
        unit: order.unit || 'kg',
        unit_price: order.unit_price || 0,
        subtotal: order.subtotal || 0,
        delivery_fee: order.delivery_fee || 0,
        total_amount: order.total_amount || order.amount || 0,
        status: order.order_status || order.status || 'pending',
        order_date: order.order_date || order.created_at || new Date().toISOString(),
        delivery_date: order.delivery_date || order.expected_delivery || new Date().toISOString(),
        delivery_address: order.delivery_address,
        contact_phone: order.contact_phone,
        special_instructions: order.special_instructions,
        payment_status: order.payment_status || 'pending',
        payment_method: order.payment_method
      }))

      setProducts(transformedProducts)
      setOrders(transformedOrders)
    } catch (error) {
      console.error('Error fetching marketplace data:', error)
      // Set empty arrays on error
      setProducts([])
      setOrders([])
    } finally {
      setLoading(false)
    }
  }

  const fetchStoreProducts = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/admin/farm-store/products`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const result = await response.json()
        if (result.success && result.data) {
          setStoreProducts(result.data.products || [])
        }
      } else {
        console.error('Failed to fetch store products')
      }
    } catch (error) {
      console.error('Error fetching store products:', error)
    }
  }

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/marketplace/categories`)
      if (response.ok) {
        const result = await response.json()
        if (result.success && result.data) {
          setCategories(result.data)
        }
      }
    } catch (error) {
      console.error('Error fetching categories:', error)
    }
  }

  useEffect(() => {
    fetchMarketplaceData()
    fetchStoreProducts()
    fetchCategories()
  }, [])

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success'
      case 'inactive': return 'default'
      case 'draft': return 'warning'
      case 'sold': return 'primary'
      case 'expired': return 'warning'
      case 'suspended': return 'error'
      case 'confirmed': return 'success'
      case 'pending': return 'warning'
      case 'delivered': return 'primary'
      case 'cancelled': return 'error'
      case 'disputed': return 'error'
      default: return 'default'
    }
  }

  const fetchProductDetails = async (productId: string) => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/admin/products/${productId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const result = await response.json()
        console.log('Product details:', result)
        setProductDetails(result.data || result)
        setViewProductDialog(true)
      } else {
        console.error('Failed to fetch product details')
        showNotification('Failed to load product details', 'error')
      }
    } catch (error) {
      console.error('Error fetching product details:', error)
      showNotification('Error loading product details', 'error')
    }
  }

  const handleAddToFarmStore = async () => {
    if (!productDetails) return
    
    try {
      setAddingToStore(true)
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/admin/products/${productDetails.id}/add-to-store`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          admin_id: localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')!).id : null
        })
      })

      if (response.ok) {
        showNotification('Product successfully added to Farm Store!', 'success')
        setViewProductDialog(false)
        fetchMarketplaceData() // Refresh the list
      } else {
        const error = await response.json()
        showNotification(error.message || 'Failed to add product to Farm Store', 'error')
      }
    } catch (error) {
      console.error('Error adding to Farm Store:', error)
      showNotification('Error adding product to Farm Store', 'error')
    } finally {
      setAddingToStore(false)
    }
  }

  const handleProductAction = async () => {
    if (!selectedProduct) return
    
    // For suspend, reason is required; for activate/approve, default reason is fine
    const reason = actionReason.trim() || (actionType === 'activate' ? 'Approved by admin' : '')
    if (actionType === 'suspend' && !reason) {
      showNotification('Please provide a reason for suspending the product.', 'warning')
      return
    }

    try {
      const token = localStorage.getItem('token')
      const endpoint = actionType === 'activate' 
        ? `/api/v1/harvests/products/${selectedProduct.id}/activate`
        : `/api/v1/harvests/products/${selectedProduct.id}/deactivate`
      
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reason: reason,
          admin_id: localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')!).id : null
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      console.log('Product action completed:', result)
      
      // Update product status locally
      setProducts(prev => prev.map(p => 
        p.id === selectedProduct.id 
          ? { ...p, status: (actionType === 'suspend' ? 'inactive' : 'active') as MarketplaceProduct['status'] }
          : p
      ))
      
      const actionLabel = actionType === 'activate' ? 'approved' : 'suspended'
      showNotification(`Product ${actionLabel} successfully!`, 'success')
      
    } catch (error: any) {
      console.error('Error updating product:', error)
      showNotification(`Failed to ${actionType} product: ${error.message || 'Please try again.'}`, 'error')
    }
    
    // Blur focused element before closing to prevent aria-hidden warning
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
    setActionDialog(false)
    setActionReason('')
    setSelectedProduct(null)
  }

  const productStats = {
    total: products.length,
    active: products.filter(p => p.status === 'active').length,
    suspended: products.filter(p => p.status === 'suspended').length,
    totalValue: products.reduce((sum, p) => sum + p.total_value, 0)
  }

  const orderStats = {
    total: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    confirmed: orders.filter(o => o.status === 'confirmed').length,
    disputed: orders.filter(o => o.status === 'disputed').length
  }

  const storeStats = {
    total: storeProducts.length,
    active: storeProducts.filter(p => p.status === 'active').length,
    inactive: storeProducts.filter(p => p.status === 'inactive').length,
    featured: storeProducts.filter(p => p.is_featured).length
  }

  const handleOpenImageOverlay = (product: MarketplaceProduct | FarmStoreProduct | any, startIndex: number = 0) => {
    let images: string[] = []
    
    if ('harvest_images' in product) {
      // MarketplaceProduct
      images = (product.harvest_images || []).map((img: any) => resolveImageUrl(img))
    } else if ('product_images' in product) {
      // FarmStoreProduct
      images = (product.product_images || []).map((img: any) => resolveImageUrl(img))
    }
    
    setOverlayImages(images)
    setCurrentImageIndex(startIndex)
    setImageOverlayOpen(true)
  }

  const handleEditStoreProduct = (product: FarmStoreProduct) => {
    setSelectedStoreProduct(product)
    setEditPrice(product.unit_price.toString())
    setEditQuantity(product.available_quantity.toString())
    setEditStoreDialog(true)
  }

  const handleUpdateStoreProduct = async () => {
    if (!selectedStoreProduct) return

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/admin/farm-store/products/${selectedStoreProduct.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          unit_price: parseFloat(editPrice),
          available_quantity: parseFloat(editQuantity)
        })
      })

      if (response.ok) {
        showNotification('Product updated successfully!', 'success')
        setEditStoreDialog(false)
        fetchStoreProducts()
      } else {
        showNotification('Failed to update product', 'error')
      }
    } catch (error) {
      console.error('Error updating store product:', error)
      showNotification('Error updating product', 'error')
    }
  }

  const handleToggleStoreStatus = async (product: FarmStoreProduct) => {
    try {
      const token = localStorage.getItem('token')
      const newStatus = product.status === 'active' ? 'inactive' : 'active'
      
      const response = await fetch(`${API_BASE_URL}/admin/farm-store/products/${product.id}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      })

      if (response.ok) {
        showNotification(`Product ${newStatus === 'active' ? 'enabled' : 'disabled'} successfully!`, 'success')
        fetchStoreProducts()
      } else {
        showNotification('Failed to update product status', 'error')
      }
    } catch (error) {
      console.error('Error toggling store product status:', error)
      showNotification('Error updating product status', 'error')
    }
  }

  const handleOpenAddToStore = async (productId: string) => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/admin/products/${productId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const result = await response.json()
        const product = result.data || result
        console.log('Product details for store:', product)
        setAddToStoreProduct(product)
        
        // Find category ID by matching category name
        const categoryMatch = categories.find(cat => 
          cat.name.toLowerCase() === product.category?.toLowerCase() ||
          cat.slug === product.category?.toLowerCase()
        )
        
        // Pre-fill form with product data
        setStoreFormData({
          product_name: product.product_name || product.name || '',
          category_id: categoryMatch?.id || '',
          unit_price: product.price_per_unit?.toString() || '',
          available_quantity: product.quantity?.toString() || '',
          minimum_order_quantity: '1',
          status: 'active',
          is_featured: false
        })
        
        setAddToStoreDialog(true)
      } else {
        showNotification('Failed to load product details', 'error')
      }
    } catch (error) {
      console.error('Error loading product:', error)
      showNotification('Error loading product', 'error')
    }
  }

  const handleSubmitAddToStore = async () => {
    if (!addToStoreProduct) return

    try {
      setAddingToStore(true)
      const token = localStorage.getItem('token')
      
      // Use the product's own ID as harvest_product_id since it comes from harvest_products table
      const payload = {
        harvest_product_id: addToStoreProduct.id || addToStoreProduct.product_id,
        product_name: storeFormData.product_name,
        category_id: storeFormData.category_id,
        unit_price: parseFloat(storeFormData.unit_price),
        unit_of_measure: addToStoreProduct.unit || 'kg',
        available_quantity: parseFloat(storeFormData.available_quantity),
        minimum_order_quantity: parseFloat(storeFormData.minimum_order_quantity),
        status: storeFormData.status,
        is_featured: storeFormData.is_featured
      }
      
      console.log('Submitting to farm store:', payload)
      
      const response = await fetch(`${API_BASE_URL}/admin/farm-store/products`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })

      if (response.ok) {
        showNotification('Product successfully added to Farm Store!', 'success')
        setAddToStoreDialog(false)
        fetchStoreProducts()
      } else {
        const error = await response.json()
        console.error('Error response:', error)
        showNotification(error.message || 'Failed to add product to Farm Store', 'error')
      }
    } catch (error) {
      console.error('Error adding to Farm Store:', error)
      showNotification('Error adding product to Farm Store', 'error')
    } finally {
      setAddingToStore(false)
    }
  }

  if (loading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>Marketplace Management</Typography>
        <LinearProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Marketplace Management
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Monitor and manage marketplace products, orders, and resolve issues
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Store color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{productStats.active}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Products
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <ShoppingCart color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{orderStats.confirmed}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Confirmed Orders
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Warning color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{orderStats.disputed}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Disputed Orders
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <AttachMoney color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">${productStats.totalValue.toLocaleString()}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Value Listed
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs for Products, Orders, and Store */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={selectedTab} onChange={(_, newValue) => setSelectedTab(newValue)}>
          <Tab label={`Products (${productStats.total})`} />
          <Tab label={`Orders (${orderStats.total})`} />
          <Tab label={`Store (${storeStats.total})`} />
        </Tabs>
      </Box>

      {/* Products Tab */}
      {selectedTab === 0 && (
        <Card>
          <CardContent>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell>Farmer</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Quantity & Price</TableCell>
                    <TableCell>Orders & Listing</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Issues</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products.map((product) => (
                    <TableRow key={product.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            component="img"
                            src={(() => {
                              if (!product.harvest_images?.[0]) return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="60" height="60"%3E%3Crect fill="%23f3f4f6" width="60" height="60"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%239ca3af" font-size="10"%3ENo Image%3C/text%3E%3C/svg%3E';
                              return resolveImageUrl(product.harvest_images[0]);
                            })()}
                            alt={product.product_name}
                            sx={{
                              width: 60,
                              height: 60,
                              objectFit: 'cover',
                              borderRadius: 1,
                              border: '1px solid #ddd'
                            }}
                            onError={(e: any) => {
                              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="60" height="60"%3E%3Crect fill="%23f3f4f6" width="60" height="60"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%239ca3af" font-size="10"%3ENo Image%3C/text%3E%3C/svg%3E'
                            }}
                          />
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {product.product_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {product.variety} • {product.images_count} images
                            </Typography>
                            {product.organic_certified && (
                              <Chip label="Organic" size="small" color="success" sx={{ ml: 1 }} />
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Avatar sx={{ mr: 2, bgcolor: 'primary.main', width: 32, height: 32 }}>
                            <Person fontSize="small" />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {product.farmer_name}
                            </Typography>
                            <Box display="flex" alignItems="center">
                              <LocationOn fontSize="small" color="disabled" />
                              <Typography variant="caption" color="text.secondary">
                                {product.location}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={product.category}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {product.quantity} {product.unit}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ${product.price_per_unit} per {product.unit}
                        </Typography>
                        <Typography variant="caption" display="block" color="success.main">
                          Total: ${product.total_value}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {product.orders} orders
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Listed: {new Date(product.listing_date).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={product.status}
                          color={getStatusColor(product.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {product.issues_reported > 0 ? (
                          <Chip
                            label={`${product.issues_reported} issues`}
                            color="error"
                            size="small"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No issues
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => fetchProductDetails(product.id)}
                          title="View product details"
                        >
                          <Visibility />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenAddToStore(product.id)}
                          title="Add to Farm Store"
                          color="primary"
                        >
                          <Store />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedProduct(product)
                            setActionType(product.status === 'active' ? 'suspend' : 'activate')
                            setActionReason(product.status === 'active' ? '' : 'Approved by admin')
                            setActionDialog(true)
                          }}
                          title={product.status === 'active' ? 'Suspend product' : 'Approve product'}
                          color={product.status === 'active' ? 'error' : 'success'}
                        >
                          {product.status === 'active' ? <Block /> : <CheckCircle />}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Orders Tab */}
      {selectedTab === 1 && (
        <Card>
          <CardContent>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Order</TableCell>
                    <TableCell>Buyer</TableCell>
                    <TableCell>Product</TableCell>
                    <TableCell>Farmer</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Payment</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {order.order_number}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(order.order_date).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {order.buyer_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {order.product_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {order.quantity} {order.unit}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {order.farmer_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          ${order.total_amount}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={order.status}
                          color={getStatusColor(order.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={order.payment_status}
                          color={getStatusColor(order.payment_status) as any}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedOrder(order)
                            setViewOrderDialog(true)
                          }}
                          title="View order details"
                        >
                          <Visibility />
                        </IconButton>
                        {order.status === 'disputed' && (
                          <IconButton
                            size="small"
                            onClick={() => console.log('Resolve dispute:', order.id)}
                          >
                            <Edit />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Store Tab */}
      {selectedTab === 2 && (
        <Card>
          <CardContent>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell>Farmer</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Price & Stock</TableCell>
                    <TableCell>Performance</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {storeProducts.map((product) => (
                    <TableRow key={product.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            component="img"
                            src={(() => {
                              if (!product.product_images?.[0]) return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="60" height="60"%3E%3Crect fill="%23f3f4f6" width="60" height="60"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%239ca3af" font-size="10"%3ENo Image%3C/text%3E%3C/svg%3E';
                              return resolveImageUrl(product.product_images[0]);
                            })()}
                            alt={product.product_name}
                            sx={{
                              width: 60,
                              height: 60,
                              objectFit: 'cover',
                              borderRadius: 1,
                              border: '1px solid #ddd'
                            }}
                            onError={(e: any) => {
                              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="60" height="60"%3E%3Crect fill="%23f3f4f6" width="60" height="60"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%239ca3af" font-size="10"%3ENo Image%3C/text%3E%3C/svg%3E'
                            }}
                          />
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {product.product_name}
                            </Typography>
                            {product.is_featured && (
                              <Chip label="Featured" size="small" color="warning" sx={{ mt: 0.5 }} />
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {product.farmer_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={product.category_name || 'Uncategorized'}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          UGX {product.unit_price.toLocaleString()} / {product.unit_of_measure}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Stock: {product.available_quantity} {product.unit_of_measure}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          Min Order: {product.minimum_order_quantity}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {product.views_count} views
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {product.orders_count} orders • {product.total_sold} sold
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={product.status}
                          color={getStatusColor(product.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleEditStoreProduct(product)}
                          title="Edit price and quantity"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleStoreStatus(product)}
                          title={product.status === 'active' ? 'Disable' : 'Enable'}
                        >
                          {product.status === 'active' ? <Block color="error" /> : <CheckCircle color="success" />}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {storeProducts.length === 0 && (
              <Box textAlign="center" py={4}>
                <Typography color="text.secondary">
                  No products in the Farm Store yet. Add products from the Products tab.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Edit Store Product Dialog */}
      <Dialog
        open={editStoreDialog}
        onClose={() => setEditStoreDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Store Product</DialogTitle>
        <DialogContent>
          {selectedStoreProduct && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {selectedStoreProduct.product_name}
              </Typography>
              <TextField
                label="Unit Price (UGX)"
                type="number"
                fullWidth
                value={editPrice}
                onChange={(e) => setEditPrice(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                label={`Available Quantity (${selectedStoreProduct.unit_of_measure})`}
                type="number"
                fullWidth
                value={editQuantity}
                onChange={(e) => setEditQuantity(e.target.value)}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditStoreDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdateStoreProduct} variant="contained" color="primary">
            Update Product
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add to Store Dialog */}
      <Dialog
        open={addToStoreDialog}
        onClose={() => setAddToStoreDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Product to Farm Store</DialogTitle>
        <DialogContent>
          {addToStoreProduct && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {addToStoreProduct.product_name || addToStoreProduct.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Farmer: {addToStoreProduct.farmer_name}
              </Typography>
              
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    label="Product Name"
                    fullWidth
                    value={storeFormData.product_name}
                    InputProps={{ readOnly: true }}
                    disabled
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Category"
                    fullWidth
                    value={storeFormData.category_id}
                    onChange={(e) => setStoreFormData({ ...storeFormData, category_id: e.target.value })}
                    required
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </TextField>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={`Available Quantity (${addToStoreProduct.unit || 'units'})`}
                    type="number"
                    fullWidth
                    value={storeFormData.available_quantity}
                    onChange={(e) => setStoreFormData({ ...storeFormData, available_quantity: e.target.value })}
                    required
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Unit Price (UGX)"
                    type="number"
                    fullWidth
                    value={storeFormData.unit_price}
                    onChange={(e) => setStoreFormData({ ...storeFormData, unit_price: e.target.value })}
                    required
                    autoFocus
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Minimum Order Quantity"
                    type="number"
                    fullWidth
                    value={storeFormData.minimum_order_quantity}
                    onChange={(e) => setStoreFormData({ ...storeFormData, minimum_order_quantity: e.target.value })}
                    required
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Status"
                    fullWidth
                    value={storeFormData.status}
                    onChange={(e) => setStoreFormData({ ...storeFormData, status: e.target.value })}
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </TextField>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center">
                    <input
                      type="checkbox"
                      id="is_featured"
                      checked={storeFormData.is_featured}
                      onChange={(e) => setStoreFormData({ ...storeFormData, is_featured: e.target.checked })}
                      style={{ marginRight: 8 }}
                    />
                    <label htmlFor="is_featured">Featured Product</label>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddToStoreDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleSubmitAddToStore} 
            variant="contained" 
            color="primary"
            disabled={addingToStore || !storeFormData.product_name || !storeFormData.category_id || !storeFormData.unit_price || !storeFormData.available_quantity || !storeFormData.minimum_order_quantity}
          >
            {addingToStore ? 'Adding...' : 'Add to Store'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Product Detail Dialog */}
      <Dialog
        open={viewProductDialog}
        onClose={() => setViewProductDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {productDetails?.product_name || 'Product Details'}
            </Typography>
            <IconButton onClick={() => setViewProductDialog(false)} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {productDetails && (
            <Box>
              {/* Product Images */}
              {(productDetails.product_images?.length > 0 || productDetails.harvest_images?.length > 0) && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Product Images
                  </Typography>
                  <Grid container spacing={2}>
                    {((productDetails.product_images?.length > 0 ? productDetails.product_images : productDetails.harvest_images) || []).map((img: any, index: number) => {
                      const fullUrl = resolveImageUrl(img)
                      return (
                        <Grid item xs={6} sm={4} md={3} key={index}>
                          <Box
                            component="img"
                            src={fullUrl}
                            alt={`Product image ${index + 1}`}
                            sx={{
                              width: '100%',
                              height: 150,
                              objectFit: 'cover',
                              borderRadius: 1,
                              border: '1px solid #ddd',
                              cursor: 'pointer',
                              '&:hover': { opacity: 0.8 }
                            }}
                            onClick={() => handleOpenImageOverlay(productDetails, index)}
                            onError={(e: any) => {
                              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E'
                            }}
                          />
                        </Grid>
                      )
                    })}
                  </Grid>
                </Box>
              )}

              {/* Basic Information */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Basic Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Product Name</Typography>
                    <Typography variant="body1" fontWeight="bold">{productDetails.product_name}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Category</Typography>
                    <Typography variant="body1">{productDetails.product_category || 'Uncategorized'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Quantity Available</Typography>
                    <Typography variant="body1" fontWeight="bold">{productDetails.available_quantity} {productDetails.unit}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Price per Unit</Typography>
                    <Typography variant="body1" fontWeight="bold" color="success.main">${productDetails.price_per_unit}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Status</Typography>
                    <Chip label={productDetails.status} color={getStatusColor(productDetails.status) as any} size="small" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Quality Grade</Typography>
                    <Typography variant="body1">{productDetails.quality_grade || 'Not specified'}</Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Farmer Information */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Farmer Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Farmer Name</Typography>
                    <Typography variant="body1" fontWeight="bold">{productDetails.farmer_name || 'Unknown'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Farmer Email</Typography>
                    <Typography variant="body1">{productDetails.farmer_email || 'Not available'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Harvest</Typography>
                    <Typography variant="body1">{productDetails.harvest_name || 'Unknown'}</Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Additional Details */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Additional Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Minimum Order Quantity</Typography>
                    <Typography variant="body1">{productDetails.minimum_order_qty} {productDetails.unit}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Packaging Type</Typography>
                    <Typography variant="body1">{productDetails.packaging_type || 'Not specified'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Shelf Life</Typography>
                    <Typography variant="body1">{productDetails.shelf_life_days ? `${productDetails.shelf_life_days} days` : 'Not specified'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Organic Certified</Typography>
                    <Typography variant="body1">{productDetails.organic_certified ? 'Yes' : 'No'}</Typography>
                  </Grid>
                  {productDetails.product_description && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">Description</Typography>
                      <Typography variant="body1">{productDetails.product_description}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewProductDialog(false)}>
            Close
          </Button>
          <Button
            onClick={handleAddToFarmStore}
            variant="contained"
            color="primary"
            startIcon={<Store />}
            disabled={addingToStore || productDetails?.status !== 'active'}
          >
            {addingToStore ? 'Adding...' : 'Add to Farm Store'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Dialog */}
      <Dialog 
        open={actionDialog} 
        onClose={() => setActionDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {actionType === 'suspend' ? 'Suspend Product' : 'Approve Product'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity={actionType === 'suspend' ? 'warning' : 'success'} sx={{ mb: 2 }}>
              {actionType === 'suspend' 
                ? 'This will remove the product from marketplace listings'
                : 'This will approve the product and make it visible to buyers in the marketplace'
              }
            </Alert>
            
            {selectedProduct && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Product: <strong>{selectedProduct.product_name}</strong> by <strong>{selectedProduct.farmer_name}</strong>
              </Alert>
            )}

            <TextField
              label={actionType === 'suspend' ? 'Reason (required)' : 'Note (optional)'}
              multiline
              rows={3}
              value={actionReason}
              onChange={(e) => setActionReason(e.target.value)}
              fullWidth
              required={actionType === 'suspend'}
              placeholder={actionType === 'suspend' ? 'Enter reason for suspending this product...' : 'Optional note for approval...'}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleProductAction}
            variant="contained"
            color={actionType === 'suspend' ? 'warning' : 'success'}
            disabled={actionType === 'suspend' && !actionReason.trim()}
          >
            {actionType === 'suspend' ? 'Suspend Product' : 'Approve Product'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Order Detail Dialog */}
      <Dialog 
        open={viewOrderDialog} 
        onClose={() => setViewOrderDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Order Details
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                {/* Order Information */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>Order Information</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Order Number</Typography>
                  <Typography variant="body1" fontWeight="bold">{selectedOrder.order_number}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Order Date</Typography>
                  <Typography variant="body1">{new Date(selectedOrder.order_date).toLocaleString()}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Chip
                    label={selectedOrder.status}
                    color={getStatusColor(selectedOrder.status) as any}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Payment Status</Typography>
                  <Chip
                    label={selectedOrder.payment_status}
                    color={getStatusColor(selectedOrder.payment_status) as any}
                    size="small"
                    variant="outlined"
                  />
                </Grid>

                {/* Buyer Information */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Buyer Information</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Name</Typography>
                  <Typography variant="body1">{selectedOrder.buyer_name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Email</Typography>
                  <Typography variant="body1">{selectedOrder.buyer_email || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Phone</Typography>
                  <Typography variant="body1">{selectedOrder.contact_phone || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Delivery Address</Typography>
                  <Typography variant="body1">{selectedOrder.delivery_address || 'N/A'}</Typography>
                </Grid>

                {/* Farmer Information */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Farmer Information</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Name</Typography>
                  <Typography variant="body1">{selectedOrder.farmer_name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Email</Typography>
                  <Typography variant="body1">{selectedOrder.farmer_email || 'N/A'}</Typography>
                </Grid>

                {/* Product Information */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Product Information</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Product</Typography>
                  <Typography variant="body1">{selectedOrder.product_name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Quantity</Typography>
                  <Typography variant="body1">{selectedOrder.quantity} {selectedOrder.unit}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Unit Price</Typography>
                  <Typography variant="body1">UGX {selectedOrder.unit_price?.toLocaleString() || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                  <Typography variant="body1">UGX {selectedOrder.subtotal?.toLocaleString() || 'N/A'}</Typography>
                </Grid>

                {/* Payment Information */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Payment Information</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Payment Method</Typography>
                  <Typography variant="body1">{selectedOrder.payment_method || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Delivery Fee</Typography>
                  <Typography variant="body1">UGX {selectedOrder.delivery_fee?.toLocaleString() || '0'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Total Amount</Typography>
                  <Typography variant="h5" color="primary" fontWeight="bold">
                    UGX {selectedOrder.total_amount.toLocaleString()}
                  </Typography>
                </Grid>

                {/* Special Instructions */}
                {selectedOrder.special_instructions && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Special Instructions</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body1">{selectedOrder.special_instructions}</Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOrderDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Overlay */}
      <ImageOverlay
        open={imageOverlayOpen}
        onClose={() => setImageOverlayOpen(false)}
        images={overlayImages}
        currentImageIndex={currentImageIndex}
        onImageChange={setCurrentImageIndex}
      />

      {/* Snackbar Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  )
}

export default Marketplace
